import { useState, useCallback } from 'react';

interface UseCopyToClipboardReturn {
  /** Function to copy text to clipboard */
  copyToClipboard: (text: string) => Promise<boolean>;
  /** Whether the text was successfully copied */
  isCopied: boolean;
  /** Error message if copy failed */
  error: string | null;
}

/**
 * Custom hook to copy text to the clipboard
 * @param resetDelay - Time in milliseconds before resetting the copied state (default: 2000ms)
 * @returns Object containing copyToClipboard function, isCopied state, and error state
 *
 * @example
 * const { copyToClipboard, isCopied } = useCopyToClipboard();
 *
 * const handleCopy = () => {
 *   copyToClipboard('text to copy');
 * };
 */
export const useCopyToClipboard = (resetDelay: number = 2000): UseCopyToClipboardReturn => {
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copyToClipboard = useCallback(
    async (text: string): Promise<boolean> => {
      if (!navigator?.clipboard) {
        setError('Clipboard API not available');

        return false;
      }

      try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);
        setError(null);

        // Reset the copied state after the specified delay
        setTimeout(() => {
          setIsCopied(false);
        }, resetDelay);

        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to copy to clipboard');
        setIsCopied(false);

        return false;
      }
    },
    [resetDelay],
  );

  return { copyToClipboard, isCopied, error };
};
